import { Alert, Button, Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { toast } from 'react-toastify';
import { useState } from 'react';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { registerEInvoiceSender } from 'src/shared/services/electronicInvoiceSenderService';
import { RegisterSenderRequest } from '../models/registerSenderRequest';

type EInvoiceConnectForm = {
  name: string;
  address1: string;
  address2: string;
  zipCode: string;
  city: string;
  cin: string;
  vat: string;
  email: string;
  phone: string;
};

type EInvoiceConnectModalProps = {
  open: boolean;
  onClose: () => void;
  invoiceGroupId: number;
  refetchCallback: () => void;
};

export const EInvoiceConnectModal = (props: EInvoiceConnectModalProps) => {
  const { open, onClose, invoiceGroupId, refetchCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);

  const schema: Schema<EInvoiceConnectForm> = yup.object().shape({
    name: yup
      .string()
      .max(50, t('common:validationMessages.maxLength', { count: 50 }))
      .required(t('common:validationMessages.required')),
    address1: yup
      .string()
      .max(50, t('common:validationMessages.maxLength', { count: 50 }))
      .required(t('common:validationMessages.required')),
    address2: yup
      .string()
      .max(50, t('common:validationMessages.maxLength', { count: 50 }))
      .default(''),
    zipCode: yup.string().matches(/^\d+$/, t('common:validationMessages.invalidFormat')).required(t('common:validationMessages.required')),
    city: yup
      .string()
      .max(50, t('common:validationMessages.maxLength', { count: 50 }))
      .required(t('common:validationMessages.required')),
    cin: yup.string().matches(/^\d+$/, t('common:validationMessages.invalidFormat')).required(t('common:validationMessages.required')),
    vat: yup
      .string()
      .test('vat-format', 'Ange i formatet SE + organisationsnummer + 01, t.ex. SE123456789001', (value) => !value || /^SE\d{12}$/.test(value))
      .default(''),
    email: yup.string().email(t('common:validationMessages.email')).required(t('common:validationMessages.required')),
    phone: yup.string().matches(/^\d*$/, t('common:validationMessages.invalidFormat')).default(''),
  });

  const formMethods = useForm<EInvoiceConnectForm>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      name: '',
      address1: '',
      address2: '',
      zipCode: '',
      city: '',
      cin: '',
      vat: '',
      email: '',
      phone: '',
    },
  });

  const { handleSubmit } = formMethods;

  const onSubmit = async (data: EInvoiceConnectForm) => {
    setLoading(true);

    const request: RegisterSenderRequest = {
      owner_id: invoiceGroupId,
      name: data.name,
      address1: data.address1,
      address2: data.address2 || '',
      zipCode: data.zipCode,
      city: data.city,
      countryCode: 'SE',
      contactName: '',
      phone: data.phone || '',
      fax: '',
      email: data.email,
      cin: data.cin.replace('-', '').replace(' ', ''),
      gln: '',
      vat: data.vat,
      bankgiro: '',
      plusGiro: '',
      iban: '',
      bic: '',
      vatNo: data.vat,
      taxCategoryId: 'S',
    };

    try {
      await registerEInvoiceSender(request);
      if (isMounted()) {
        toast.success(<>{t('invoice:adminInvoiceGroupDetailPage.eInvoiceConnectSuccess')}</>);
        refetchCallback();
        onClose();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('invoice:adminInvoiceGroupDetailPage.eInvoiceConnectError')}</>);
      }
    } finally {
      if (isMounted()) setLoading(false);
    }
  };

  return (
    <GenericDialog
      maxWidth='sm'
      disableBackdropClosing
      open={open}
      dialogTitle={t('invoice:adminInvoiceGroupDetailPage.connectEInvoice')}
      dialogContent={
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2} p={0}>
              <LoadingOverlay loading={loading} />
              <Grid item xs={12}>
                <Alert
                  icon={false}
                  severity='info'
                  sx={{
                    '& .MuiAlert-message': { width: '100% !important' },
                    fontWeight: 500,
                    m: -2,
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {t('invoice:adminInvoiceGroupDetailPage.eInvoiceConnectDescription')}
                </Alert>
              </Grid>
              <Grid item xs={12} mt={2}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.eInvoiceCompanyName')} name='name' />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.eInvoiceAddress')} name='address1' />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.eInvoiceAddress2')} name='address2' />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.eInvoiceZipCode')} name='zipCode' />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.eInvoiceCity')} name='city' />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.orgNr')} name='cin' />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.eInvoiceVat')} name='vat' />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.eInvoiceEmail')} name='email' />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField label={t('invoice:adminInvoiceGroupDetailPage.eInvoicePhone')} name='phone' />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      }
      dialogActions={
        <Button variant='contained' color='primary' disableElevation onClick={() => formMethods.handleSubmit(onSubmit)()}>
          {t('invoice:adminInvoiceGroupDetailPage.connectEInvoice')}
        </Button>
      }
      onClose={onClose}
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      onSave={() => {}}
    />
  );
};
